// Developer: Ankit Gupta
import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
} from 'axios';
import Keycloak from 'keycloak-js';
import { currentLanguage } from '@/i18n';

// ─── Shared Keycloak reference ────────────────────────────────────────────────
// The Axios interceptor needs access to the Keycloak token. We store a
// reference here so that AuthProvider can inject the live instance after init.

let _keycloak: Keycloak | null = null;

export function setKeycloakInstance(kc: Keycloak) {
  _keycloak = kc;
}

export function keycloakInstance(): Keycloak | null {
  return _keycloak;
}

// ─── Axios instance ───────────────────────────────────────────────────────────

const baseURL =
  import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ─── Request interceptor: attach Bearer token ─────────────────────────────────

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // The server answers its messages, and has its language model write its
    // prose, in this language. It never changes the data it returns.
    config.headers.set('Accept-Language', currentLanguage());
    if (_keycloak?.authenticated) {
      // Proactively refresh the token if it expires within 30 seconds
      try {
        await _keycloak.updateToken(30);
      } catch {
        // Token refresh failed – force a new login below
        _keycloak.login({ locale: currentLanguage() });
        return Promise.reject(new Error('Token refresh failed'));
      }

      if (_keycloak.token) {
        config.headers.set('Authorization', `Bearer ${_keycloak.token}`);
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ─── Response interceptor: handle 401 ────────────────────────────────────────

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401 && _keycloak?.authenticated) {
      // A session existed and its token was rejected or expired: sign in again. Without a
      // session (the public landing page, a call made before sign-in) a 401 is just an
      // answer; sending it to the login form would turn the front door into a redirect.
      _keycloak.login({ locale: currentLanguage() });
    }
    return Promise.reject(error);
  },
);

// ─── Typed helpers ────────────────────────────────────────────────────────────

// Server caps page_size at 200 (see backend deps.PaginationDep).
const MAX_PAGE_SIZE = 200;

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    apiClient.get<T>(url, { params }).then((r) => r.data),

  post: <T>(url: string, data?: unknown) =>
    apiClient.post<T>(url, data).then((r) => r.data),

  put: <T>(url: string, data?: unknown) =>
    apiClient.put<T>(url, data).then((r) => r.data),

  patch: <T>(url: string, data?: unknown) =>
    apiClient.patch<T>(url, data).then((r) => r.data),

  delete: <T>(url: string) =>
    apiClient.delete<T>(url).then((r) => r.data),

  /**
   * Fetch every page of a paginated `{items, total, page, page_size}` endpoint
   * and return the merged list of items. Respects MAX_PAGE_SIZE (200).
   * Use sparingly - large result sets should be displayed via real pagination.
   */
  getAll: async <T>(
    url: string,
    params?: Record<string, unknown>,
    pageSize: number = MAX_PAGE_SIZE,
  ): Promise<T[]> => {
    const size = Math.min(pageSize, MAX_PAGE_SIZE);
    const all: T[] = [];
    let page = 1;
    while (true) {
      const resp = await apiClient
        .get<{ items: T[]; total: number }>(url, {
          params: { ...(params ?? {}), page, page_size: size },
        })
        .then((r) => r.data);
      const items = resp?.items ?? [];
      all.push(...items);
      if (items.length < size || all.length >= (resp?.total ?? all.length)) break;
      page += 1;
      if (page > 100) break; // safety cap: 100 pages = 20k items
    }
    return all;
  },
};

export default apiClient;
